import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buttonVariants } from '@/components/ui/Button';
import { PLAN_BUDGETS } from '@shared/constants/billing';
import { useSubscriptionQuery, useTokenUsageQuery } from './api';

/*
 * BudgetWarningBanner (Sessio 19a).
 *
 * Renderoidaan AppLayout:n yli (TrialBanner / DpaBanner -pattern) kun org:n
 * kuluvan kauden token-kulutus saavuttaa 80 %. Kayttoaste lasketaan client-
 * puolella reaaliaikaisesti (useTokenUsageQuery + PLAN_BUDGETS) jotta varoitus
 * nakyy heti -- ei odoteta budget-monitor-cronin paivittamaa server-lippua.
 *
 *  - >=80 %  -> oranssi banneri ("lahes taynna")
 *  - >=100 % -> tummempi oranssi ("kaytetty loppuun, AI-toiminnot estetty")
 *
 * Itse 100%-esto tapahtuu backendissa (lib/ai/budget.ts checkBudget). Tama on
 * vain informatiivinen banneri. Allowance-pakit (token_budget_allowances) eivat
 * viela vaikuta laskentaan -- ne tulevat Sessio 19b:n token-pack-ostoon.
 */

const WARNING_THRESHOLD = 0.8;

export function BudgetWarningBanner() {
  const { t } = useTranslation();
  const { data: subscription } = useSubscriptionQuery();
  const { data: usage } = useTokenUsageQuery();

  if (!subscription || !usage) return null;

  const budget = PLAN_BUDGETS[subscription.plan];
  if (!budget) return null;

  // Ostetut lisatokenit lisataan budjettiin (kuten checkBudget) -- pakin osto
  // laskee kayttoastetta, ettei varoitus jaa nakyviin turhaan.
  const totalInput = budget.input + (usage.allowance_input_tokens ?? 0);
  const totalOutput = budget.output + (usage.allowance_output_tokens ?? 0);
  const pct = Math.max(
    totalInput > 0 ? usage.total_input_tokens / totalInput : 0,
    totalOutput > 0 ? usage.total_output_tokens / totalOutput : 0,
  );
  if (pct < WARNING_THRESHOLD) return null;

  const level = pct >= 1 ? 'critical' : 'warning';
  const percent = Math.round(pct * 100);
  const colorClasses =
    level === 'critical'
      ? 'border-oranssi bg-oranssi/20 text-teksti'
      : 'border-oranssi bg-oranssi/10 text-teksti';

  return (
    <div
      role="region"
      aria-label={t('billing.budget_warning.regionLabel')}
      className={['border-b px-6 py-3 text-sm lg:px-10', colorClasses].join(' ')}
    >
      <div className="mx-auto flex max-w-[var(--content-max)] flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <strong className="font-semibold">
          {t(`billing.budget_warning.${level}`, { percent })}
        </strong>
        <Link to="/billing" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          {t('billing.budget_warning.cta')}
        </Link>
      </div>
    </div>
  );
}
